import { css } from 'aphrodite';
import { useId } from 'react';
import type { AssistantEmbedPosition } from '../../../../../utils/assistantUrlUtils';
import { MessageCircleIcon } from '../../../../../web/components/svgs/icons/MessageCircleIcon';
import {
	CustomerAssistantAvatar,
	CustomerAssistantContent,
	CustomerAssistantConversationStarterCard,
	CustomerAssistantConversationStarters,
	CustomerAssistantDescription,
	CustomerAssistantTitle,
} from '../../CustomerAssistantUI';
import { previewWidgetStyles } from './AssistantPreviewWidget.styles';
import { embeddedPreviewStyles as s } from './EmbeddedPreviewWidget.styles';

interface EmbeddedPreviewWidgetProps {
	name: string;
	description: string;
	avatarUrl: string | null;
	conversationStarters: string[];
	position: AssistantEmbedPosition;
	launcherLabel: string;
}

export function EmbeddedPreviewWidget({
	name,
	description,
	avatarUrl,
	conversationStarters,
	position,
	launcherLabel,
}: EmbeddedPreviewWidgetProps) {
	const id = useId();
	const nonEmptyStarters = conversationStarters.filter(starter => starter.trim());
	const trimmedLabel = launcherLabel.trim();
	const hasLabel = trimmedLabel.length > 0;
	const isLeft = position === 'bottom-left';

	return (
		<div className={css(s.backdrop)}>
			<div className={css(s.backdropPattern)} aria-hidden='true' />

			{/* Widget root — anchored bottom-left or bottom-right */}
			<div className={css(s.widgetRoot, isLeft ? s.widgetRootLeft : s.widgetRootRight)}>
				{/* Launcher button */}
				<div className={css(s.launcher, hasLabel && s.launcherWithLabel)} aria-hidden='true'>
					<MessageCircleIcon />
					{hasLabel ? <span className={css(s.launcherLabel)}>{trimmedLabel}</span> : null}
				</div>

				{/* Popup panel */}
				<div className={css(s.panel)}>
					<div className={css(s.panelClose)} aria-hidden='true'>
						&times;
					</div>
					<div className={css(s.panelContent)}>
						<CustomerAssistantContent styles={[previewWidgetStyles.container]}>
							{avatarUrl ? <CustomerAssistantAvatar imgSrc={avatarUrl} /> : null}
							<CustomerAssistantTitle styles={[previewWidgetStyles.name, !name && previewWidgetStyles.namePlaceholder]}>
								{name || 'Assistant Name'}
							</CustomerAssistantTitle>
							<CustomerAssistantDescription
								styles={[previewWidgetStyles.description, !description && previewWidgetStyles.descriptionPlaceholder]}
							>
								{description || 'Assistant description will appear here...'}
							</CustomerAssistantDescription>
							{nonEmptyStarters.length > 0 && (
								<CustomerAssistantConversationStarters styles={[previewWidgetStyles.conversationStartersContainer]}>
									{nonEmptyStarters.map((starter, index) => (
										<CustomerAssistantConversationStarterCard key={`${id}-starter-${index}`}>
											{starter}
										</CustomerAssistantConversationStarterCard>
									))}
								</CustomerAssistantConversationStarters>
							)}
						</CustomerAssistantContent>
					</div>
				</div>
			</div>
		</div>
	);
}
